import clsx from 'clsx'
import { XClose } from '@/assets/icons'
import { Tooltip } from '@/components/ui/tooltip/Tooltip'
import { useIsTruncated } from '@/components/ui/tooltip/useIsTruncated'
import type { TypeaheadChipProps } from './types'

/**
 * TypeaheadChip Component
 *
 * A removable chip displaying a selected item within the Typeahead input.
 * Uses tonal neutral styling consistent with Tag component patterns.
 * The label truncates with ellipsis when it's wider than the available space (the row by default, or a
 * tighter `maxWidth` if the caller sets one), and the full text shows in a tooltip only when clipped.
 */
export function TypeaheadChip({ label, onRemove, disabled = false, maxWidth = 'max-w-full', forceTooltipOpen = false }: TypeaheadChipProps) {
  const { ref: labelRef, isTruncated } = useIsTruncated<HTMLSpanElement>()

  return (
    <span
      data-ds-component="TypeaheadChip"
      data-testid="typeahead-chip"
      className={clsx(
        'inline-flex items-center gap-1 pl-2.5 pr-2 py-1 rounded',
        maxWidth,
        'bg-surface-content border border-border-default text-text-secondary text-sm leading-5',
        disabled && 'opacity-50'
      )}
    >
      <Tooltip title={isTruncated || forceTooltipOpen ? label : ''} placement="top" offsetY={-12} forceOpen={forceTooltipOpen}>
        <span ref={labelRef} className="truncate">
          {label}
        </span>
      </Tooltip>
      {!disabled && (
        // Don't migrate this to IconButton: the design specs this remove control outside
        // IconButton's sizes and hover treatment (18px circle, brand-neutral-100 fill).
        <button
          type="button"
          aria-label={`Remove ${label}`}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className={clsx(
            'flex size-[18px] shrink-0 items-center justify-center rounded-full text-text-tertiary transition-colors',
            'hover:bg-surface-hover',
            'focus:outline-hidden focus-visible:outline focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-neutral-500'
          )}
        >
          <XClose className="size-4" aria-hidden />
        </button>
      )}
    </span>
  )
}
